import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import {
  WalletIcon,
  ChartIcon,
  ArrowDownIcon,
  MailIcon,
  XIcon,
} from "../components/Icons";
import { walletAPI, userAPI } from "../services/api";
import Toast from "../components/Toast";
import { getApiErrorMessage } from "../utils/apiError";

const Withdrawal = () => {
  const [balance, setBalance] = useState(0);
  const [profitBalance, setProfitBalance] = useState(0);
  const [mainBalance, setMainBalance] = useState(0);
  const [withdrawalMode, setWithdrawalMode] = useState("profit");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user2FAEnabled, setUser2FAEnabled] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    method: "",
    destination: "",
  });
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAToken, setTwoFAToken] = useState("");
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [withdrawOTP, setWithdrawOTP] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [networkFee, setNetworkFee] = useState(0);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [showWithdrawPausedModal, setShowWithdrawPausedModal] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Fetch wallet balance
  useEffect(() => {
    fetchWalletBalance();
    checkUser2FAStatus();
    fetchWithdrawalHistory();
  }, []);

  const checkUser2FAStatus = async () => {
    try {
      // Always read fresh status from backend profile.
      const profile = await userAPI.getProfile();
      const enabled =
        typeof profile?.twoFAEnabled === "boolean"
          ? profile.twoFAEnabled
          : Boolean(profile?.user?.twoFAEnabled);
      setUser2FAEnabled(enabled);

      // Keep local cache in sync for other screens.
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({ ...userData, twoFAEnabled: enabled }),
      );
    } catch (error) {
      console.error("Failed to check 2FA status:", error);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      setLoading(true);
      const walletData = await walletAPI.getWallet();
      const w = walletData.wallet || {};
      setBalance(Number(w.balance || 0));
      setProfitBalance(Number(w.profitBalance ?? 0));
      setMainBalance(Number(w.mainBalance ?? 0));
    } catch (error) {
      console.error("Failed to load wallet balance:", error);
      setBalance(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await walletAPI.getWithdrawals({ limit: 50 });
      setWithdrawalHistory(response.data?.withdrawals || []);
    } catch (error) {
      console.error("Failed to load withdrawal history:", error);
      setWithdrawalHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const showMessage = (type, text) => {
    const variant = type === "error" ? "error" : "success";
    setToast({ show: true, message: text, variant });
    setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      variant === "error" ? 8000 : 4500,
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (withdrawalMode === "principal_refund") {
      const ok = window.confirm(
        "Refund requests are subject to a 25% deduction, inclusive of all earnings generated. Continue?",
      );
      if (!ok) return;
    }

    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      showMessage("error", "Please enter a valid amount");
      return;
    }
    if (!formData.method) {
      showMessage("error", "Please select a network");
      return;
    }
    if (!formData.destination) {
      showMessage("error", "Please enter withdrawal address");
      return;
    }

    // Check if 2FA is required
    if (user2FAEnabled) {
      setShow2FAModal(true);
      return;
    }

    // Proceed without 2FA
    await processWithdrawalRequest();
  };

  const processWithdrawalRequest = async (twoFACode = null) => {
    try {
      setSubmitting(true);

      const requestBody = {
        amount: parseFloat(formData.amount),
        network: formData.method,
        address: formData.destination,
      };

      // Add 2FA token if provided
      if (twoFACode) {
        requestBody.twoFAToken = twoFACode;
      }

      // Request OTP
      const response = await walletAPI.requestWithdrawalOTP(
        requestBody.amount,
        requestBody.network,
        requestBody.address,
        requestBody.twoFAToken || null,
        withdrawalMode === "principal_refund" ? "principal_refund" : "profit",
      );

      setNetworkFee(response.networkFee || 0);
      setOtpExpiry(response.expiresAt);
      setShow2FAModal(false);
      setShowOTPModal(true);
      setTwoFAToken("");
      showMessage("success", response.message || "OTP sent to your email");
    } catch (error) {
      if (error.requires2FA) {
        setShow2FAModal(true);
        showMessage("error", "2FA verification required");
      } else if (
        String(getApiErrorMessage(error, ""))
          .toLowerCase()
          .includes("withdrawals are currently paused")
      ) {
        setShowWithdrawPausedModal(true);
      } else {
        showMessage(
          "error",
          getApiErrorMessage(error, "Failed to request withdrawal"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handle2FASubmit = () => {
    if (twoFAToken.length !== 6) {
      showMessage("error", "Please enter a valid 6-digit code");
      return;
    }
    processWithdrawalRequest(twoFAToken);
  };

  const handleVerifyOTP = async () => {
    if (!withdrawOTP || withdrawOTP.length !== 6) {
      showMessage("error", "Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setSubmitting(true);
      const response = await walletAPI.verifyWithdrawalOTP(withdrawOTP);
      showMessage("success", "Payment in process");

      // Reset form
      setFormData({
        amount: "",
        method: "",
        destination: "",
      });
      setWithdrawOTP("");
      setShowOTPModal(false);

      // Refresh balance and withdrawal history
      fetchWalletBalance();
      fetchWithdrawalHistory();
    } catch (error) {
      showMessage(
        "error",
        getApiErrorMessage(error, "OTP verification failed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const closeOTPModal = () => {
    setShowOTPModal(false);
    setWithdrawOTP("");
  };

  const getStatusStyle = (status) => {
    const statusUpper = status?.toUpperCase() || "";

    if (statusUpper.includes("CONFIRMED") || statusUpper === "COMPLETED") {
      return "bg-gradient-to-r from-[#22c55e]/20 to-[#16a34a]/20 text-primary border border-primary/30";
    } else if (statusUpper.includes("PENDING")) {
      return "bg-yellow-500/20 text-yellow-400 border border-yellow-400/30";
    } else if (statusUpper.includes("PROCESSING")) {
      return "bg-[#22c55e]/10 text-[#16a34a] border border-primary/20";
    } else if (
      statusUpper.includes("FAILED") ||
      statusUpper.includes("REJECTED")
    ) {
      return "bg-red-500/20 text-red-400 border border-red-400/30";
    } else {
      return "bg-gray-500/20 text-[var(--text-secondary)] border border-gray-400/30";
    }
  };

  const formatStatus = (status) => {
    const statusUpper = status?.toUpperCase() || "";

    // Simplify status messages
    if (statusUpper.includes("PENDING")) {
      return "Pending";
    } else if (statusUpper.includes("PROCESSING")) {
      return "Payment in Process";
    } else if (
      statusUpper.includes("CONFIRMED") ||
      statusUpper === "COMPLETED"
    ) {
      return "Completed";
    } else if (statusUpper.includes("FAILED")) {
      return "Failed";
    } else if (statusUpper.includes("REJECTED")) {
      return "Rejected";
    }

    return status || "Unknown";
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const limitsFeesData = [
    {
      icon: <ArrowDownIcon className="w-5 h-5" />,
      label: "Minimum (opBNB BNB)",
      value: "$1",
    },
    {
      icon: <ArrowDownIcon className="w-5 h-5" />,
      label: "Minimum (BEP20 BNB)",
      value: "$20",
    },
    {
      icon: <ArrowDownIcon className="w-5 h-5" />,
      label: "Minimum (TRC20 USDT)",
      value: "$50",
    },
    {
      icon: <WalletIcon className="w-5 h-5" />,
      label: "Standard withdrawals",
      value: "Profit wallet only",
    },
  ];

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 pt-16 md:p-8 md:pt-8 md:ml-64">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Withdrawal
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            Withdraw your funds securely to your preferred method
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Left Column - Request Withdrawal Form */}
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Request a Withdrawal
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary)] mb-2">
                    Withdrawal type
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="wdMode"
                        checked={withdrawalMode === "profit"}
                        onChange={() => setWithdrawalMode("profit")}
                      />
                      <span className="text-[var(--text-primary)] text-sm">
                        Profit wallet (standard)
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="wdMode"
                        checked={withdrawalMode === "principal_refund"}
                        onChange={() => setWithdrawalMode("principal_refund")}
                      />
                      <span className="text-[var(--text-primary)] text-sm">
                        Principal refund (25% deduction applies)
                      </span>
                    </label>
                  </div>
                </div>

                {/* Amount to Withdraw */}
                <Input
                  type="number"
                  name="amount"
                  label="Amount to Withdraw"
                  placeholder="e.g., 500.00"
                  value={formData.amount}
                  onChange={handleInputChange}
                  icon={<WalletIcon className="w-5 h-5" />}
                />

                {/* Withdrawal Method */}
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Withdrawal Method (Network)
                  </label>
                  <select
                    name="method"
                    value={formData.method}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary transition-colors"
                  >
                    <option value="">Select a network</option>
                    <option value="TRC20">USDT-TRC20 (Tron Network)</option>
                    <option value="BEP20">USDT-BEP20 (BSC Network)</option>
                    <option value="OPBNB">USDT — OPBNB (opBNB)</option>
                  </select>
                </div>

                {/* Destination Address / Account Number */}
                <Input
                  type="text"
                  name="destination"
                  label="Destination Address / Account Number"
                  placeholder="Enter wallet address or bank account"
                  value={formData.destination}
                  onChange={handleInputChange}
                  icon={<MailIcon className="w-5 h-5" />}
                />

                {/* Warning Message */}
                <div className="bg-yellow-500/10 border border-yellow-500/50 p-4 rounded-xl">
                  <p className="text-xs text-yellow-400">
                    ⚠️ Double-check your wallet address. Incorrect addresses may
                    result in permanent loss of funds. Verified withdrawals are
                    processed on-chain automatically.
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={submitting || loading}
                  className="w-full py-4 text-lg font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Processing..." : "Request Withdrawal OTP"}
                </Button>
              </form>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Available Balance Card */}
            <Card>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                  Wallet buckets
                </h3>
                {loading ? (
                  <p className="text-lg text-[var(--text-primary)]">Loading...</p>
                ) : (
                  <div className="space-y-2 text-[var(--text-primary)] text-sm">
                    <p>
                      <span className="opacity-70">Profit (withdrawable):</span>{" "}
                      <span className="font-bold text-green-400">
                        {profitBalance.toFixed(2)} USDT
                      </span>
                    </p>
                    <p>
                      <span className="opacity-70">Main (principal):</span>{" "}
                      <span className="font-semibold">{mainBalance.toFixed(2)} USDT</span>
                    </p>
                    <p>
                      <span className="opacity-70">Total:</span>{" "}
                      <span className="font-semibold">{balance.toFixed(2)} USDT</span>
                    </p>
                  </div>
                )}
                <p className="text-[var(--text-primary)] text-xs opacity-70 mt-3">
                  Standard withdrawals use your profit wallet only. Principal refunds
                  debit your main wallet (see 25% policy).
                </p>
              </div>
              <Button className="w-full hover:shadow-lg hover:shadow-primary/20 transition-all">
                Withdraw Now
              </Button>
            </Card>

            {/* Limits & Fees Card */}
            <Card>
              <h3 className="text-lg font-semibold mb-4 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                Limits & Fees
              </h3>
              <div className="space-y-4">
                {limitsFeesData.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#22c55e]/20 to-[#16a34a]/20 flex items-center justify-center flex-shrink-0">
                      <div className="bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                        {item.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[var(--text-primary)] opacity-70 mb-1">
                        {item.label}
                      </p>
                      <p className="font-semibold text-[var(--text-primary)]">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Withdrawal History */}
        <Card>
          <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Withdrawal History
          </h2>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Transaction ID
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Date
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Amount
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Method
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Status
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan="6" className="py-8 text-center">
                      <div className="flex justify-center items-center space-x-2">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-[var(--text-primary)]">
                          Loading withdrawal history...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : withdrawalHistory.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="py-8 text-center text-[var(--text-primary)] opacity-70"
                    >
                      No withdrawal transactions found
                    </td>
                  </tr>
                ) : (
                  withdrawalHistory.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm text-[var(--text-primary)]">
                          {transaction.txHash ||
                            transaction.transactionId ||
                            `WD${transaction.id}`}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)]">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-semibold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                          ${parseFloat(transaction.amount || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)]">
                        {transaction.network || "Crypto"}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusStyle(
                            transaction.status,
                          )}`}
                        >
                          {formatStatus(transaction.status)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-[var(--text-primary)]">
                        ${parseFloat(transaction.networkFee || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {historyLoading ? (
              <div className="flex justify-center items-center py-8 space-x-2">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-[var(--text-primary)]">Loading withdrawal history...</span>
              </div>
            ) : withdrawalHistory.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-primary)] opacity-70">
                No withdrawal transactions found
              </div>
            ) : (
              withdrawalHistory.map((transaction) => (
                <div
                  key={transaction.id}
                  className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-mono text-sm text-[var(--text-primary)] mb-1">
                        {transaction.txHash ||
                          transaction.transactionId ||
                          `WD${transaction.id}`}
                      </p>
                      <p className="text-xs text-[var(--text-primary)] opacity-70">
                        {formatDate(transaction.createdAt)}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusStyle(
                        transaction.status,
                      )}`}
                    >
                      {formatStatus(transaction.status)}
                    </span>
                  </div>
                  <div className="mb-3">
                    <p className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                      ${parseFloat(transaction.amount || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">
                        Network
                      </p>
                      <p className="text-sm text-[var(--text-primary)]">
                        {transaction.network || "Crypto"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">Fee</p>
                      <p className="text-sm text-[var(--text-primary)]">
                        ${parseFloat(transaction.networkFee || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Toast
          message={toast.message}
          show={toast.show}
          variant={toast.variant}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />

        {/* 2FA Verification Modal */}
        {show2FAModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22c55e] to-[#16a34a] flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[var(--text-primary)]">
                    Two-Factor Authentication
                  </h3>
                </div>
                <button
                  onClick={() => setShow2FAModal(false)}
                  className="text-[var(--text-secondary)] hover:text-white transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                <p className="text-sm text-[var(--text-secondary)]">
                  Enter your 6-digit authentication code to authorize this
                  withdrawal.
                </p>

                {/* 2FA Code Input */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--text-primary)] mb-3">
                    Authentication Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={twoFAToken}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setTwoFAToken(value);
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-3 bg-surface-soft border-2 border-primary rounded-xl text-white text-3xl text-center tracking-[0.5em] font-bold focus:outline-none focus:ring-2 focus:ring-[#22c55e] transition-all"
                  />
                  <p className="text-xs text-[var(--text-secondary)] mt-2">
                    Open your authenticator app to get the code
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShow2FAModal(false)}
                    className="flex-1 py-3 bg-surface-softer hover:bg-white/10 text-white rounded-xl font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handle2FASubmit}
                    disabled={twoFAToken.length !== 6}
                    className="flex-1 py-3 bg-gradient-to-r from-[#22c55e] to-[#16a34a] hover:shadow-lg hover:shadow-primary/20 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Verify & Continue
                  </button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* OTP Verification Modal */}
        {showOTPModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)]">Verify OTP</h3>
                <button
                  onClick={closeOTPModal}
                  className="text-[var(--text-secondary)] hover:text-white transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-[var(--text-secondary)]">
                  We've sent a 6-digit verification code to your email. Please
                  enter it below to confirm your withdrawal.
                </p>

                {/* Withdrawal Summary */}
                <div className="bg-gradient-to-br from-[#22c55e]/10 to-[#16a34a]/10 border border-primary/20 p-4 rounded-xl">
                  <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-3">
                    Withdrawal Summary
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">Amount:</span>
                      <span className="font-semibold text-[var(--text-primary)]">
                        ${formData.amount}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--text-secondary)]">Network Fee:</span>
                      <span className="font-semibold text-[var(--text-primary)]">
                        ${networkFee.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-primary/20 pt-2">
                      <span className="text-[var(--text-secondary)]">Total Deducted:</span>
                      <span className="font-bold text-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                        ${(parseFloat(formData.amount) + networkFee).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Network and Address */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-secondary)]">Network:</span>
                    <span className="font-semibold text-[var(--text-primary)]">
                      {formData.method}
                    </span>
                  </div>
                  <div>
                    <span className="text-[var(--text-secondary)]">Address:</span>
                    <p className="font-mono text-xs text-[var(--text-primary)] break-all mt-1">
                      {formData.destination}
                    </p>
                  </div>
                </div>

                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-semibold text-[var(--text-primary)] mb-3">
                    Enter 6-digit OTP
                  </label>
                  <input
                    type="text"
                    maxLength="6"
                    placeholder="000000"
                    value={withdrawOTP}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setWithdrawOTP(value);
                    }}
                    className="w-full px-4 py-4 rounded-xl bg-white dark:bg-[var(--bg-card)] border-2 border-primary text-center text-3xl tracking-[0.5em] font-bold text-gray-900 dark:text-[var(--text-primary)] focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#22c55e]/50 transition-all"
                    autoComplete="off"
                  />
                </div>

                {/* OTP Expiry */}
                {otpExpiry && (
                  <p className="text-xs text-center text-[var(--text-secondary)]">
                    OTP expires at {new Date(otpExpiry).toLocaleTimeString()}
                  </p>
                )}

                {/* Verify Button */}
                <Button
                  onClick={handleVerifyOTP}
                  disabled={submitting || withdrawOTP.length !== 6}
                  className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Verifying..." : "Verify & Submit Withdrawal"}
                </Button>

                <button
                  onClick={closeOTPModal}
                  className="w-full py-2 text-sm text-[var(--text-secondary)] hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </Card>
          </div>
        )}

        {showWithdrawPausedModal && (
          <div className="fixed inset-0 bg-overlay flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                  Withdrawals Temporarily Paused
                </h3>
                <button
                  onClick={() => setShowWithdrawPausedModal(false)}
                  className="text-[var(--text-secondary)] hover:text-white transition-colors"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-white/80 mb-3">
                Withdrawal requests are currently paused by admin settings.
              </p>
              <p className="text-primary text-sm mb-6">
                Please contact admin/support for activation and try again later.
              </p>
              <Button
                onClick={() => setShowWithdrawPausedModal(false)}
                className="w-full"
              >
                I Understand
              </Button>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Withdrawal;
